import React, { useMemo } from 'react';
import { Grid, Typography, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TimePickerField from '../common/TimePickerField';
import WeekdaySelect from '../common/WeekdaySelect';
import RadioGroupField from '../common/RadioGroupField';
import CheckboxTimePicker from '../common/CheckboxTimePicker';
import { useZone, useZoneActions } from '../ZoneContext';

// ============================================================================
// 5. DIFERENCIAL FORM UNIFICADO (Daily + Weekly)
// ============================================================================

const DiferencialForm = React.memo(({ type }) => {
    const { t } = useTranslation();
    const zone = useZone();
    const { updateField } = useZoneActions();

    // Determinar si es weekly o daily
    const isWeekly = type === 'diferential_weekly_extend_daily';
    const data = isWeekly ? zone.diferentialWeekly : zone.diferentialDaily;
    const prefix = isWeekly ? 'Weekly' : '';

    const section = isWeekly ? 'diferentialWeekly' : 'diferentialDaily';

    // Opciones para based by
    const basedByOptions = useMemo(() => [
        {
            value: 'real_punch',
            label: t(`settings.zones.fields.diferencial_${isWeekly ? 'weekend' : 'daily'}.minimum_hours.qualifiers.based_on.real_punch`)
        },
        {
            value: 'schedule',
            label: t(`settings.zones.fields.diferencial_${isWeekly ? 'weekend' : 'daily'}.minimum_hours.qualifiers.based_on.schedule`)
        }
    ], [t, isWeekly]);

    // Prefijo para los campos
    const fieldPrefix = isWeekly ? 'weekend' : 'daily';

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Start Time/Day */}
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TimePickerField
                    id={`start${prefix}`}
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.start`)}
                    value={data[`start${prefix}`]}
                    onChange={(value) => updateField(section, `start${prefix}`, value)}
                />
            </Grid>

            {/* Start Day (solo para weekly) */}
            {isWeekly && (
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <WeekdaySelect
                        value={data.weekday1Weekly}
                        onChange={(e) => updateField(section, 'weekday1Weekly', e.target.value)}
                        label={t('settings.zones.fields.diferencial_weekend.start_day')}
                    />
                </Grid>
            )}

            {/* End Time */}
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TimePickerField
                    id={`end${prefix}`}
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.end`)}
                    value={data[`end${prefix}`]}
                    onChange={(value) => updateField(section, `end${prefix}`, value)}
                />
            </Grid>

            {/* End Day (solo para weekly) */}
            {isWeekly && (
                <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <WeekdaySelect
                        value={data.weekday2Weekly}
                        onChange={(e) => updateField(section, 'weekday2Weekly', e.target.value)}
                        label={t('settings.zones.fields.diferencial_weekend.start_day')}
                    />
                </Grid>
            )}

            {/* Minimum Hours Section */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                    {t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.title`)}
                </Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TimePickerField
                    id={`within-zone${prefix}`}
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.within_the_zone`)}
                    value={data[`withinZone${prefix}`]}
                    onChange={(value) => updateField(section, `withinZone${prefix}`, value)}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TimePickerField
                    id={`within-turn${prefix}`}
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.within_the_turn`)}
                    value={data[`withinTurn${prefix}`]}
                    onChange={(value) => updateField(section, `withinTurn${prefix}`, value)}
                />
            </Grid>

            {/* Expansion Margins */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                    {t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.expansion_margins`)}
                </Typography>
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TimePickerField
                    id={`early${prefix}`}
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.early`)}
                    value={data[`early${prefix}`]}
                    onChange={(value) => updateField(section, `early${prefix}`, value)}
                />
            </Grid>

            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TimePickerField
                    id={`late${prefix}`}
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.late`)}
                    value={data[`late${prefix}`]}
                    onChange={(value) => updateField(section, `late${prefix}`, value)}
                />
            </Grid>

            {/* Qualifiers Section */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                    {t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.title`)}
                </Typography>
            </Grid>

            {/* Based By */}
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <RadioGroupField
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.based_on.title`)}
                    value={data[`basedBy${prefix}`]}
                    onChange={(e) => updateField(section, `basedBy${prefix}`, e.target.value)}
                    options={basedByOptions}
                    name={`based-by-${section}`}
                    disabled={!(data[`beforeStart${prefix}`] || data[`beforeFinish${prefix}`] || data[`afterStart${prefix}`] || data[`afterFinish${prefix}`])}
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={data[`planned`]}
                            onChange={(e) => updateField(section, `planned`, e.target.checked)}
                        />
                    }
                    label={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.based_on.unplanned`)}
                />
            </Grid>

            {/* Must Start Working */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth component="fieldset" variant="standard">
                    <FormLabel component="legend">
                        {t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_start_working.title`)}
                    </FormLabel>
                    <FormGroup>
                        <CheckboxTimePicker
                            checkboxLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_start_working.before`)}
                            timeLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_start_working.before`)}
                            checked={data[`beforeStart${prefix}`]}
                            onCheckboxChange={(e) => updateField(section, `beforeStart${prefix}`, e.target.checked)}
                            timeValue={data[`beforeTimeStart${prefix}`]}
                            onTimeChange={(value) => updateField(section, `beforeTimeStart${prefix}`, value)}
                        />

                        <CheckboxTimePicker
                            checkboxLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_start_working.after`)}
                            timeLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_start_working.after`)}
                            checked={data[`afterStart${prefix}`]}
                            onCheckboxChange={(e) => updateField(section, `afterStart${prefix}`, e.target.checked)}
                            timeValue={data[`afterTimeStart${prefix}`]}
                            onTimeChange={(value) => updateField(section, `afterTimeStart${prefix}`, value)}
                        />
                    </FormGroup>
                </FormControl>
            </Grid>

            {/* Must Finish Working */}
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth component="fieldset" variant="standard">
                    <FormLabel component="legend">
                        {t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_finish_working.title`)}
                    </FormLabel>
                    <FormGroup>
                        <CheckboxTimePicker
                            checkboxLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_finish_working.before`)}
                            timeLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_finish_working.before`)}
                            checked={data[`beforeFinish${prefix}`]}
                            onCheckboxChange={(e) => updateField(section, `beforeFinish${prefix}`, e.target.checked)}
                            timeValue={data[`beforeTimeFinish${prefix}`]}
                            onTimeChange={(value) => updateField(section, `beforeTimeFinish${prefix}`, value)}
                        />

                        <CheckboxTimePicker
                            checkboxLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_finish_working.after`)}
                            timeLabel={t(`settings.zones.fields.diferencial_${fieldPrefix}.minimum_hours.qualifiers.must_finish_working.after`)}
                            checked={data[`afterFinish${prefix}`]}
                            onCheckboxChange={(e) => updateField(section, `afterFinish${prefix}`, e.target.checked)}
                            timeValue={data[`afterTimeFinish${prefix}`]}
                            onTimeChange={(value) => updateField(section, `afterTimeFinish${prefix}`, value)}
                        />
                    </FormGroup>
                </FormControl>
            </Grid>
        </Grid>
    );
});

const DiferencialDaily = React.memo(() => (
  <DiferencialForm type="diferential_daily_extend_zone_rule" />
));

const DiferencialWeekly = React.memo(() => (
  <DiferencialForm type="diferential_weekly_extend_daily" />
));


export { DiferencialDaily, DiferencialWeekly };